import React from 'react';
import type { PiV2Plan } from '../../../patient-insurance/typesV2';
import { PI_V2_ROLE_LABEL, piV2EligibilityText } from '../../../patient-insurance/utilsV2';
import { Button } from '../../Button/Button';
import { Icon } from '../../Icon/Icon';
import { Pill } from '../../Pill/Pill';

export interface PatientInsurancePlanCardV2Props {
  plan: PiV2Plan;
  onReVerify?: (planId: string) => void;
  /** True while an electronic re-check is in flight for this plan (spec §8.5). */
  verifying?: boolean;
}

/**
 * One plan card. Three changes from v1:
 *
 * — No "Card" button: there is no stored card image.
 * — Eligibility is a state with a date, not a baked "Verified · 2 days ago"
 *   string. `failed` and `never` read differently because they need different
 *   actions — a payer problem versus a data-entry gap.
 * — `role` comes from the data, so tertiary renders like any other plan.
 */
export function PatientInsurancePlanCardV2({
  plan,
  onReVerify,
  verifying,
}: PatientInsurancePlanCardV2Props) {
  const eligibility = piV2EligibilityText(plan.eligibility);
  const fields: [string, string, boolean?][] = [
    ['Member ID', plan.memberId, true],
    ['Group', plan.group, true],
    ['Subscriber', plan.subscriber],
    ['Relationship', plan.relationship],
    ['Effective', plan.effective],
    ['Carrier phone', plan.carrierPhone, true],
  ];

  return (
    <div className="relative flex min-w-0 flex-col overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-2)]">
      <div
        className="relative px-5 py-[18px] text-white"
        style={{
          background: `linear-gradient(135deg, color-mix(in srgb, ${plan.accent} 90%, #000), color-mix(in srgb, ${plan.accent} 60%, #000))`,
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] opacity-80">
              {PI_V2_ROLE_LABEL[plan.role]} · {plan.planType}
            </div>
            <div className="mt-1 truncate font-[var(--font-display)] text-[20px] font-bold tracking-[-0.02em]">
              {plan.carrier}
            </div>
            <div className="mt-px truncate text-[12.5px] opacity-90">{plan.planName}</div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-[99px] bg-white/20 px-2.5 py-1 text-[10.5px] font-bold">
            <span className="h-1.5 w-1.5 rounded-full bg-white" />
            {plan.inNetwork ? 'In-network' : 'Out-of-network'}
          </span>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-2 gap-x-4 gap-y-3 px-5 py-4">
        {fields.map(([label, value, mono]) => (
          <div key={label} className="min-w-0">
            <div className="font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.1em] text-[var(--ink-4)]">
              {label}
            </div>
            <div
              className="mt-0.5 truncate text-[13px] font-semibold text-[var(--ink-0)]"
              style={{ fontFamily: mono === true ? 'var(--font-mono)' : 'var(--font-body)' }}
            >
              {value}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 border-t border-[var(--cream-2)] bg-[var(--cream-0)] px-5 py-[11px]">
        <Pill tone={eligibility.tone} dot={plan.eligibility.state === 'ok'}>
          {eligibility.label}
        </Pill>
        {plan.eligibility.state === 'failed' ? (
          <Icon kind="alert" size={13} stroke={2} style={{ color: 'var(--gold)' }} />
        ) : null}
        <span className="ml-auto">
          <Button
            variant="secondary"
            size="sm"
            iconLeft="refresh"
            disabled={verifying === true}
            onClick={() => {
              onReVerify?.(plan.id);
            }}
          >
            {verifying === true ? 'Verifying…' : 'Re-verify'}
          </Button>
        </span>
      </div>
    </div>
  );
}

/**
 * Placeholder for adding another plan. Deliberately promises nothing about
 * card scanning: whether MMS extraction works is unverified, and a promise the
 * product cannot keep is worse than a plain button.
 */
export function PatientInsuranceAddPlanV2({ nextRole }: { nextRole: string }) {
  return (
    <button
      type="button"
      className="flex min-h-[180px] cursor-pointer flex-col items-center justify-center gap-2.5 rounded-[var(--r-xl)] border-[1.5px] border-dashed border-[var(--cream-shadow)] bg-transparent p-6 font-[var(--font-body)]"
    >
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--ai-tint)] text-[var(--ai-3)]">
        <Icon kind="plus" size={22} stroke={2} />
      </span>
      <span className="font-[var(--font-display)] text-base font-semibold text-[var(--ink-0)]">
        Add {nextRole} insurance
      </span>
    </button>
  );
}
